import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Pencil, Trash2, Building2, GraduationCap, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const emptyClient = {
  organization_name: "", contact_name: "", position: "", phone: "", email: "",
  industry: "", address: "", training_needs: "", notes: "",
};

const ClientForm = ({ open, onOpenChange, initial, onSubmit, mode }) => {
  const [form, setForm] = useState(initial || emptyClient);
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (v) => { if (v) setForm(initial || emptyClient); onOpenChange(v); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try { await onSubmit(form); handleOpenChange(false); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{mode === "create" ? "Add client" : "Edit client"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-2 md:col-span-2">
            <Label>Organization *</Label>
            <Input data-testid="client-org-input" value={form.organization_name} onChange={(e) => setF("organization_name", e.target.value)} />
          </div>
          <div className="space-y-2"><Label>Contact Name *</Label><Input data-testid="client-contact-input" value={form.contact_name} onChange={(e) => setF("contact_name", e.target.value)} /></div>
          <div className="space-y-2"><Label>Position</Label><Input value={form.position} onChange={(e) => setF("position", e.target.value)} /></div>
          <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} /></div>
          <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} /></div>
          <div className="space-y-2"><Label>Industry</Label><Input value={form.industry} onChange={(e) => setF("industry", e.target.value)} /></div>
          <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setF("address", e.target.value)} /></div>
          <div className="space-y-2 md:col-span-2"><Label>Training Needs</Label><Textarea rows={2} value={form.training_needs} onChange={(e) => setF("training_needs", e.target.value)} /></div>
          <div className="space-y-2 md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setF("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="client-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.organization_name || !form.contact_name} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="client-form-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ClientDetailDialog = ({ clientId, open, onOpenChange }) => {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (open && clientId) {
      api.get(`/clients/${clientId}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    } else { setDetail(null); }
  }, [open, clientId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-detail-dialog">
        {detail ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{detail.client.organization_name}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 text-sm mt-3">
              <div><div className="text-xs text-[#6B5944]">Contact</div><div className="font-medium">{detail.client.contact_name}</div></div>
              <div><div className="text-xs text-[#6B5944]">Position</div><div className="font-medium">{detail.client.position || "—"}</div></div>
              <div><div className="text-xs text-[#6B5944]">Email</div><div className="font-medium">{detail.client.email || "—"}</div></div>
              <div><div className="text-xs text-[#6B5944]">Phone</div><div className="font-medium">{detail.client.phone || "—"}</div></div>
              <div><div className="text-xs text-[#6B5944]">Industry</div><div className="font-medium">{detail.client.industry || "—"}</div></div>
              <div><div className="text-xs text-[#6B5944]">Address</div><div className="font-medium">{detail.client.address || "—"}</div></div>
            </div>
            {detail.client.training_needs && (
              <div className="mt-4">
                <div className="text-xs text-[#6B5944] mb-1">Training Needs</div>
                <div className="text-sm bg-[#FDFBF5] p-3 rounded-lg border border-[#EBE3D2]">{detail.client.training_needs}</div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#C28A2E] mb-2">
                  <GraduationCap className="w-3.5 h-3.5" /> Workshops ({detail.workshops.length})
                </div>
                <div className="space-y-2">
                  {detail.workshops.map((w) => (
                    <div key={w.id} className="text-sm border border-[#EBE3D2] rounded-lg p-3">
                      <div className="font-medium">{w.name}</div>
                      <div className="text-xs text-[#6B5944]">{w.date} · {w.status}</div>
                    </div>
                  ))}
                  {detail.workshops.length === 0 && <div className="text-xs text-[#6B5944]">No workshops yet</div>}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#C28A2E] mb-2">
                  <FileText className="w-3.5 h-3.5" /> Proposals ({detail.proposals.length})
                </div>
                <div className="space-y-2">
                  {detail.proposals.map((p) => (
                    <div key={p.id} className="text-sm border border-[#EBE3D2] rounded-lg p-3">
                      <div className="font-medium">{p.proposal_number}</div>
                      <div className="text-xs text-[#6B5944]">${Number(p.amount).toLocaleString()} · {p.status}</div>
                    </div>
                  ))}
                  {detail.proposals.length === 0 && <div className="text-xs text-[#6B5944]">No proposals yet</div>}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="py-12 text-center text-[#6B5944]">Loading…</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchClients = () => {
    setLoading(true);
    api.get("/clients").then((r) => setClients(r.data)).finally(() => setLoading(false));
  };
  useEffect(fetchClients, []);

  // Auto-open detail dialog when arriving via `?open=<client_id>` (e.g., from Lead-to-Client conversion)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setDetailId(openId);
      setDetailOpen(true);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filtered = clients.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.organization_name || "").toLowerCase().includes(q) || (c.contact_name || "").toLowerCase().includes(q);
  });

  const onCreate = async (data) => {
    try { await api.post("/clients", data); toast.success("Client added"); fetchClients(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); throw e; }
  };
  const onUpdate = async (data) => {
    try { await api.put(`/clients/${editing.id}`, data); toast.success("Client updated"); fetchClients(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); throw e; }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`); toast.success("Deleted"); fetchClients();
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Relationships</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5">Clients</h1>
          <p className="text-[#6B5944] mt-1.5">Every organization you serve, with full history at your fingertips.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-client-btn"
          className="bg-[#C28A2E] hover:bg-[#A6741F] text-white rounded-xl h-11 px-5 hover:-translate-y-[1px] transition-all">
          <Plus className="w-4 h-4 mr-2" /> Add client
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B5944]" />
        <Input data-testid="clients-search-input" placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-11 rounded-xl bg-white border-[#EBE3D2]" />
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#6B5944]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="ccs-card p-12 text-center">
          <Building2 className="w-10 h-10 text-[#C28A2E] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display text-xl font-medium text-[#2A1810]">No clients yet</div>
          <p className="text-[#6B5944] text-sm mt-1">Add your first client to start building lasting partnerships.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} className="ccs-card ccs-card-hover p-5" data-testid={`client-card-${c.id}`}>
              <button onClick={() => { setDetailId(c.id); setDetailOpen(true); }} className="text-left w-full">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-xl bg-[#5D3A1F]/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#5D3A1F]" strokeWidth={1.7} />
                  </div>
                  {c.industry && <Badge variant="outline" className="text-[10px] border-[#EBE3D2] text-[#6B5944]">{c.industry}</Badge>}
                </div>
                <div className="font-display text-lg font-semibold mt-4 text-[#2A1810]">{c.organization_name}</div>
                <div className="text-sm text-[#6B5944] mt-0.5">{c.contact_name}{c.position ? ` · ${c.position}` : ""}</div>
                {c.email && <div className="text-xs text-[#6B5944] mt-2">{c.email}</div>}
              </button>
              <div className="flex justify-end gap-1 mt-3 -mr-2">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setFormOpen(true); }} data-testid={`edit-client-${c.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onDelete(c.id)} data-testid={`delete-client-${c.id}`}>
                  <Trash2 className="w-4 h-4 text-[#A23E3E]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ClientForm open={formOpen} onOpenChange={setFormOpen} initial={editing}
        mode={editing ? "edit" : "create"} onSubmit={editing ? onUpdate : onCreate} />
      <ClientDetailDialog clientId={detailId} open={detailOpen} onOpenChange={setDetailOpen} />
    </div>
  );
};

export default ClientsPage;
